import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import './App.css';

function App() {
  const [currentTab, setCurrentTab] = useState('schools_approved');
  const [data, setData] = useState(null);
  const [yesterdayData, setYesterdayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [warningsExpanded, setWarningsExpanded] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'sickPercentage', direction: 'desc' });
  const [logs, setLogs] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [logSearch, setLogSearch] = useState('');
  
  const dataRef = useRef();
  const yesterdayDataRef = useRef();
  const lastUpdateRef = useRef();
  const wsRef = useRef();

  const tabs = [
    { key: 'schools_approved', label: 'Школы' },
    { key: 'schools_current', label: 'Школы тек.' },
    { key: 'kindergartens_approved', label: 'Сады' },
    { key: 'kindergartens_current', label: 'Сады тек.' },
    { key: 'logs', label: 'Логи' }
  ];

  const isSchoolTab = () => {
    return (currentTab === 'schools_approved' || currentTab === 'schools_current') && currentTab !== 'logs';
  };

  const isKindergartenTab = () => {
    return (currentTab === 'kindergartens_approved' || currentTab === 'kindergartens_current') && currentTab !== 'logs';
  };

  // WebSocket соединение
  useEffect(() => {
    if (currentTab === 'logs') {
      connectWebSocket();
    } else {
      disconnectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [currentTab]);

  const connectWebSocket = () => {
  try {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
   
    const wsUrl = `${protocol}//statapi.paragraf.spb.ru/ws`;
    
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onopen = () => {
      console.log('WebSocket connected to statapi.paragraf.spb.ru');
      setWsConnected(true);
    };
    
    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      // Попытка переподключения через 5 секунд
      setTimeout(() => {
        if (currentTab === 'logs') {
          connectWebSocket();
        }
      }, 5000);
    };
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };
    
  } catch (error) {
    console.error('WebSocket connection error:', error);
    setWsConnected(false);
  }
};

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  };

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'new_log':
        setLogs(prevLogs => [message.data, ...prevLogs].slice(0, 1000));
        break;
      case 'logs_history':
        setLogs(message.data);
        break;
      case 'pong':
        // Обработка pong сообщения
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  const sendWebSocketMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  const clearLogs = () => {
    sendWebSocketMessage({ type: 'clear_logs' });
    setLogs([]);
  };

  const getFilteredLogs = () => {
    let filtered = [...logs];
    
    if (logFilter !== 'all') {
      filtered = filtered.filter(log => log.level === logFilter);
    }
    
    if (logSearch) {
      const searchLower = logSearch.toLowerCase();
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  };

  const getLogLevelIcon = (level) => {
    switch (level) {
      case 'error': return '🔴';
      case 'warn': return '🟡';
      case 'info': return '🔵';
      default: return '⚪';
    }
  };

  const formatLogTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const extractInstitutionNumber = (name) => {
    if (!name) return '';
    
    const matches = name.match(/\d+/g);
    if (matches && matches.length > 0) {
      return matches[0];
    }
    
    return '';
  };

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      
      const response = await fetch('https://statapi.paragraf.spb.ru/api/data/latest');
      if (!response.ok) {
        throw new Error('Ошибка загрузки данных');
      }
      const currentData = await response.json();
      
      // Обновляем ref вместо state для фонового обновления
      dataRef.current = currentData;
      lastUpdateRef.current = new Date();
      
      // Если это первоначальная загрузка, устанавливаем state
      if (loading) {
        setData(currentData);
        setLoading(false);
        
        // Загружаем вчерашние данные только при первоначальной загрузке
        const yesterdayResponse = await fetch('https://statapi.paragraf.spb.ru/api/data/yesterday');
        if (yesterdayResponse.ok) {
          const yesterdayData = await yesterdayResponse.json();
          setYesterdayData(yesterdayData);
          yesterdayDataRef.current = yesterdayData;
        }
        
        setLastUpdate(lastUpdateRef.current);
      } else {
        // Для фонового обновления используем requestAnimationFrame для плавного обновления
        requestAnimationFrame(() => {
          setData(dataRef.current);
          setLastUpdate(lastUpdateRef.current);
        });
      }
      
    } catch (err) {
      setError(err.message);
      if (loading) {
        setLoading(false);
      }
    }
  }, [loading]);

  useEffect(() => {
    // Первоначальная загрузка данных
    fetchData();

    // Настраиваем интервал для автоматического обновления
    const intervalId = setInterval(fetchData, 60000);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  const getCurrentData = () => {
    const currentData = data || dataRef.current;
    if (!currentData || !currentData.data) return null;
    
    const currentTabData = currentData.data[currentTab];
    if (!currentTabData) return null;

    if (currentTabData.latest_data?.data) {
      return currentTabData.latest_data.data;
    } else if (currentTabData.data) {
      return currentTabData.data;
    }
    
    return null;
  };

  const getYesterdayData = () => {
    const yesterdayDataValue = yesterdayData || yesterdayDataRef.current;
    if (!yesterdayDataValue || !yesterdayDataValue.data) {
      return null;
    }
    
    const yesterdayTabData = yesterdayDataValue.data[currentTab];
    if (!yesterdayTabData) {
      return null;
    }

    const extractData = (dataObj) => {
      if (!dataObj) return null;
      
      if (dataObj.data?.data?.data && Array.isArray(dataObj.data.data.data)) {
        return {
          data: dataObj.data.data.data,
          total: dataObj.data.data.total
        };
      }
      
      if (dataObj.data?.data && Array.isArray(dataObj.data.data)) {
        return {
          data: dataObj.data.data,
          total: dataObj.data.total
        };
      }
      
      if (dataObj.data && Array.isArray(dataObj.data)) {
        return {
          data: dataObj.data,
          total: dataObj.total
        };
      }
      
      if (dataObj.latest_data?.data?.data && Array.isArray(dataObj.latest_data.data.data)) {
        return {
          data: dataObj.latest_data.data.data,
          total: dataObj.latest_data.data.total
        };
      }
      
      return null;
    };

    let result = extractData(yesterdayTabData);
    
    if (!result && yesterdayTabData.data) {
      result = extractData({ data: yesterdayTabData.data });
    }
    
    return result;
  };

  const calculateSickPercentage = (sick, total) => {
    if (!total || total === 0) return 0;
    return parseFloat(((sick / total) * 100).toFixed(1));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Нет данных';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(',', '');
    } catch (error) {
      return 'Неверный формат';
    }
  };

  const isBeforeToday1500 = (dateString) => {
    if (!dateString) return false;
    
    try {
      const institutionDate = new Date(dateString);
      const today = new Date();
      
      const today1500 = new Date(today);
      today1500.setHours(15, 0, 0, 0);
      
      return institutionDate < today1500;
    } catch (error) {
      return false;
    }
  };

  const isBeforeToday = (dateString) => {
    if (!dateString) return false;
    
    try {
      const institutionDate = new Date(dateString);
      const today = new Date();
      
      const todayStart = new Date(today);
      todayStart.setHours(0, 0, 0, 0);
      
      return institutionDate < todayStart;
    } catch (error) {
      return false;
    }
  };

  const formatUpdateTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotalGroups = (data) => {
    if (!data || !data.data) return 0;
    return data.data.reduce((total, institution) => total + parseInt(institution.n_groups || 0), 0);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!data || !data.data) return data;

    const sortedData = [...data.data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'number':
          aValue = parseInt(extractInstitutionNumber(a.name_short) || 0);
          bValue = parseInt(extractInstitutionNumber(b.name_short) || 0);
          break;
        case 'name':
          aValue = a.name_short || '';
          bValue = b.name_short || '';
          break;
        case 'date':
          aValue = new Date(a.date_modify || 0).getTime();
          bValue = new Date(b.date_modify || 0).getTime();
          break;
        case 'groups':
          aValue = parseInt(a.n_groups || 0);
          bValue = parseInt(b.n_groups || 0);
          break;
        case 'groupsDo':
          aValue = parseInt(a.n_groups_do || 0);
          bValue = parseInt(b.n_groups_do || 0);
          break;
        case 'students':
          aValue = parseInt(a.n_students || 0);
          bValue = parseInt(b.n_students || 0);
          break;
        case 'illStudents':
          aValue = parseInt(a.ill_students || 0);
          bValue = parseInt(b.ill_students || 0);
          break;
        case 'sickPercentage':
          aValue = calculateSickPercentage(parseInt(a.ill_students || 0), parseInt(a.n_students || 1));
          bValue = calculateSickPercentage(parseInt(b.ill_students || 0), parseInt(b.n_students || 1));
          if (isNaN(aValue)) aValue = 0;
          if (isNaN(bValue)) bValue = 0;
          break;
        case 'illEmployees':
          aValue = parseInt(a.ill_employees || 0);
          bValue = parseInt(b.ill_employees || 0);
          break;
        case 'missed':
          aValue = parseInt(a.n_miss || 0);
          bValue = parseInt(b.n_miss || 0);
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return {
      ...data,
      data: sortedData
    };
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const exportToExcel = () => {
    const currentData = getCurrentData();
    if (!currentData) return;

    const worksheetData = [
      [
        '№ ОО',
        'Наименование организации',
        'Дата обновления',
        'Кол-во коллективов',
        'Коллективов на ДО',
        'Кол-во обучающихся',
        'Заболевших обучающихся',
        '% заболевших',
        'Заболевших сотрудников',
        'Отсутствовало'
      ],
      ...currentData.data.map(institution => [
        extractInstitutionNumber(institution.name_short),
        institution.name_short,
        formatDateTime(institution.date_modify),
        institution.n_groups,
        institution.n_groups_do,
        institution.n_students,
        institution.ill_students,
        calculateSickPercentage(parseInt(institution.ill_students), parseInt(institution.n_students)) + '%',
        institution.ill_employees,
        institution.n_miss
      ]),
      [],
      ['ИТОГО:', '', '', '', '', '', '', '', '', ''],
      [
        '',
        '',
        '',
        currentData.total?.n_groups || 0,
        currentData.total?.n_groups_do || 0,
        currentData.total?.n_students || 0,
        currentData.total?.ill_students || 0,
        calculateSickPercentage(parseInt(currentData.total?.ill_students || 0), parseInt(currentData.total?.n_students || 1)) + '%',
        currentData.total?.ill_employees || 0,
        currentData.total?.n_miss || 0
      ]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    
    if (!worksheet['!cols']) worksheet['!cols'] = [];
    worksheet['!cols'][0] = { width: 10 };
    worksheet['!cols'][1] = { width: 30 };
    worksheet['!cols'][2] = { width: 20 };
    
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4472C4' } }
    };
    
    for (let col = 0; col < 10; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellRef]) continue;
      worksheet[cellRef].s = headerStyle;
    }
    
    const totalStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'F2F2F2' } }
    };
    
    for (let col = 0; col < 10; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: worksheetData.length - 2, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = totalStyle;
      }
      
      const cellRef2 = XLSX.utils.encode_cell({ r: worksheetData.length - 1, c: col });
      if (worksheet[cellRef2]) {
        worksheet[cellRef2].s = totalStyle;
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Данные');
    
    const now = new Date();
    const fileName = `мониторинг_${currentTab}_${now.toISOString().split('T')[0]}_${now.getHours()}${now.getMinutes()}.xlsx`;
    
    XLSX.writeFile(workbook, fileName);
  };

  const getWarnings = () => {
    const warnings = [];
    const current = getCurrentData();
    const yesterday = getYesterdayData();

    if (current?.total && yesterday?.total) {
      const currentTotalGroups = parseInt(current.total.n_groups || 0);
      const yesterdayTotalGroups = parseInt(yesterday.total.n_groups || 0);
      
      if (currentTotalGroups !== yesterdayTotalGroups) {
        warnings.push(`Общее кол-во коллективов: ${yesterdayTotalGroups} → ${currentTotalGroups}`);
      }

      const currentTotalDo = parseInt(current.total.n_groups_do || 0);
      const yesterdayTotalDo = parseInt(yesterday.total.n_groups_do || 0);
      
      if (currentTotalDo !== yesterdayTotalDo) {
        warnings.push(`Общее кол-во ДО: ${yesterdayTotalDo} → ${currentTotalDo}`);
      }

      const calculatedCurrentTotal = calculateTotalGroups(current);
      if (currentTotalGroups !== calculatedCurrentTotal) {
        warnings.push(`Расхождение в итогах: ${currentTotalGroups} ≠ ${calculatedCurrentTotal}`);
      }
    }

    if (current?.data && yesterday?.data && Array.isArray(current.data) && Array.isArray(yesterday.data)) {
      const currentMap = new Map();
      const yesterdayMap = new Map();

      current.data.forEach(inst => {
        if (inst?.id) {
          currentMap.set(inst.id, inst);
        }
      });

      yesterday.data.forEach(inst => {
        if (inst?.id) {
          yesterdayMap.set(inst.id, inst);
        }
      });

      currentMap.forEach((currentInst, id) => {
        const yesterdayInst = yesterdayMap.get(id);
        
        if (yesterdayInst) {
          const currentGroups = parseInt(currentInst.n_groups || 0);
          const yesterdayGroups = parseInt(yesterdayInst.n_groups || 0);
          
          if (currentGroups !== yesterdayGroups) {
            warnings.push(`${currentInst.name_short}: коллективов ${yesterdayGroups} → ${currentGroups}`);
          }

          const currentDo = parseInt(currentInst.n_groups_do || 0);
          const yesterdayDo = parseInt(yesterdayInst.n_groups_do || 0);
          
          if (currentDo !== yesterdayDo) {
            warnings.push(`${currentInst.name_short}: ДО ${yesterdayDo} → ${currentDo}`);
          }

          const currentStudents = parseInt(currentInst.n_students || 0);
          const yesterdayStudents = parseInt(yesterdayInst.n_students || 0);
          
          if (currentStudents !== yesterdayStudents && Math.abs(currentStudents - yesterdayStudents) > 5) {
            warnings.push(`${currentInst.name_short}: учащихся ${yesterdayStudents} → ${currentStudents}`);
          }
        }
      });

      yesterdayMap.forEach((yesterdayInst, id) => {
        if (!currentMap.has(id)) {
          warnings.push(`Отсутствует учреждение: ${yesterdayInst.name_short}`);
        }
      });

      currentMap.forEach((currentInst, id) => {
        if (!yesterdayMap.has(id)) {
          warnings.push(`Новое учреждение: ${currentInst.name_short}`);
        }
      });
    }

    if (current?.data && Array.isArray(current.data)) {
      current.data.forEach(institution => {
        if (!institution) return;
        
        const sickPercentage = calculateSickPercentage(
          parseInt(institution.ill_students || 0),
          parseInt(institution.n_students || 1)
        );
        
        if (sickPercentage > 25) {
          warnings.push(`${institution.name_short}: ${sickPercentage.toFixed(1)}% > 25%`);
        }

        if (isSchoolTab() && isBeforeToday1500(institution.date_modify)) {
          warnings.push(`${institution.name_short}: неактуальные данные`);
        }

        if (isKindergartenTab() && isBeforeToday(institution.date_modify)) {
          warnings.push(`${institution.name_short}: неактуальные данные`);
        }

        if (parseInt(institution.ill_students || 0) > parseInt(institution.n_students || 0)) {
          warnings.push(`${institution.name_short}: больных больше чем учащихся`);
        }
      });
    }

    return warnings;
  };

  if (loading && !data) {
    return (
      <div className="app">
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="app">
        <div className="error">Ошибка: {error}</div>
        <button onClick={fetchData} className="retry-btn">
          Повторить
        </button>
      </div>
    );
  }

  const currentData = getCurrentData();
  const yesterdayDataForTab = getYesterdayData();
  const warnings = getWarnings();
  const sortedData = currentData ? getSortedData(currentData) : null;
  const filteredLogs = getFilteredLogs();

  return (
    <div className="app">
      <header className="header">
        <h1>Мониторинг ОУ</h1>
        {lastUpdate && currentTab !== 'logs' && (
          <div className="last-update">
            Обновлено: {formatUpdateTime(lastUpdate)}
          </div>
        )}
        {currentTab === 'logs' && (
          <div className={`ws-status ${wsConnected ? 'connected' : 'disconnected'}`}>
            {wsConnected ? '🔗 Подключено' : '🔌 Отключено'}
          </div>
        )}
      </header>

      <nav className="tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`tab ${currentTab === tab.key ? 'active' : ''}`}
            onClick={() => setCurrentTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
        {currentTab !== 'logs' && (
          <button onClick={exportToExcel} className="export-btn" title="Экспорт в Excel">
            📊
          </button>
        )}
      </nav>

      {currentTab === 'logs' ? (
        <div className="logs-tab">
          <div className="logs-controls">
            <div className="logs-filters">
              <select 
                value={logFilter} 
                onChange={(e) => setLogFilter(e.target.value)}
                className="log-filter-select"
              >
                <option value="all">Все уровни</option>
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="error">Error</option>
              </select>
              
              <input
                type="text"
                placeholder="Поиск по логам..."
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
                className="log-search-input"
              />
              
              <button onClick={clearLogs} className="clear-logs-btn">
                🗑️ Очистить
              </button>
            </div>
            
            <div className="logs-stats">
              Всего: {logs.length} | Показано: {filteredLogs.length}
            </div>
          </div>

          <div className="logs-container">
            {filteredLogs.length === 0 ? (
              <div className="no-logs">
                {logs.length === 0 ? 'Логи отсутствуют' : 'Логи не найдены'}
              </div>
            ) : (
              <div className="logs-list">
                {filteredLogs.map((log, index) => (
                  <div key={log.id || index} className={`log-item log-${log.level}`}>
                    <div className="log-header">
                      <span className="log-level">{getLogLevelIcon(log.level)} {log.level}</span>
                      <span className="log-time">{formatLogTimestamp(log.timestamp)}</span>
                    </div>
                    <div className="log-message">{log.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {warnings.length > 0 && (
            <div className="warnings">
              <div className="warnings-header" onClick={() => setWarningsExpanded(!warningsExpanded)}>
                <h3>
                  ⚠️ {warnings.length} предупреждений
                  <span className="collapse-icon">
                    {warningsExpanded ? '▼' : '►'}
                  </span>
                </h3>
              </div>
              {warningsExpanded && (
                <div className="warnings-content">
                  {warnings.map((warning, index) => {
                    let icon = 'ℹ️';
                    if (warning.includes('> 25%') || warning.includes('больных больше')) {
                      icon = '🔴';
                    } else if (warning.includes('→') || warning.includes('Отсутствует') || warning.includes('Новое')) {
                      icon = '🔴';
                    } else if (warning.includes('данные до') || warning.includes('данные не сегодня')) {
                      icon = '🟠';
                    }
                    
                    return (
                      <div key={index} className="warning-item">
                        {icon} {warning}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!currentData && !loading && (
            <div className="no-data">
              Нет данных для отображения. Проверьте структуру данных.
            </div>
          )}

          {sortedData && sortedData.data && sortedData.total && (
            <div className="content">
              <div className="summary">
                <h2>Общее</h2>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="label">Коллективы:</span>
                    <span className="value">
                      {sortedData.total.n_groups || 0}
                      {yesterdayDataForTab?.total && ` (${yesterdayDataForTab.total.n_groups || 0})`}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">На ДО:</span>
                    <span className="value">
                      {sortedData.total.n_groups_do || 0}
                      {yesterdayDataForTab?.total && ` (${yesterdayDataForTab.total.n_groups_do || 0})`}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Обучающиеся:</span>
                    <span className="value">{sortedData.total.n_students || 0}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Заболели:</span>
                    <span className="value">{sortedData.total.ill_students || 0}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">%:</span>
                    <span className="value">
                      {calculateSickPercentage(
                        parseInt(sortedData.total.ill_students || 0),
                        parseInt(sortedData.total.n_students || 1)
                      ).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="details">
                <div className="table-header">
                  <h2>Учреждения</h2>
                  <button onClick={exportToExcel} className="export-btn-small" title="Экспорт в Excel">
                    📊 Экспорт
                  </button>
                </div>
                <div className="table-container">
                  <table className="institutions-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('number')} className="sortable" title="Номер ОО">
                          № ОО {getSortIcon('number')}
                        </th>
                        <th onClick={() => handleSort('name')} className="sortable" title="Наименование">
                          Учреждение {getSortIcon('name')}
                        </th>
                        <th onClick={() => handleSort('date')} className="sortable" title="Дата обновления">
                          Обновлено {getSortIcon('date')}
                        </th>
                        <th onClick={() => handleSort('groups')} className="sortable" title="Коллективы">
                          Кол. {getSortIcon('groups')}
                        </th>
                        <th onClick={() => handleSort('groupsDo')} className="sortable" title="На ДО">
                          ДО {getSortIcon('groupsDo')}
                        </th>
                        <th onClick={() => handleSort('students')} className="sortable" title="Обучающиеся">
                          Уч. {getSortIcon('students')}
                        </th>
                        <th onClick={() => handleSort('illStudents')} className="sortable" title="Заболели">
                          Бол. {getSortIcon('illStudents')}
                        </th>
                        <th onClick={() => handleSort('sickPercentage')} className="sortable sorted" title="% заболевших">
                          % {getSortIcon('sickPercentage')}
                        </th>
                        <th onClick={() => handleSort('illEmployees')} className="sortable" title="Сотрудники">
                          Сотр. {getSortIcon('illEmployees')}
                        </th>
                        <th onClick={() => handleSort('missed')} className="sortable" title="Отсутствовало">
                          Отс. {getSortIcon('missed')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.data.map((institution, index) => {
                        const sickPercentage = calculateSickPercentage(
                          parseInt(institution.ill_students || 0),
                          parseInt(institution.n_students || 1)
                        );
                        
                        const isSchoolOldData = isSchoolTab() && isBeforeToday1500(institution.date_modify);
                        const isKindergartenOldData = isKindergartenTab() && isBeforeToday(institution.date_modify);
                        const isOldData = isSchoolOldData || isKindergartenOldData;
                        
                        return (
                          <tr 
                            key={institution.id || index}
                            className={
                              `${sickPercentage > 25 ? 'high-sickness' : ''} ${
                                isOldData ? 'old-data' : ''
                              }`
                            }
                          >
                            <td className="institution-number">
                              {extractInstitutionNumber(institution.name_short)}
                            </td>
                            <td className="institution-name" title={institution.name_short}>
                              {institution.name_short?.replace('ГБОУ ', '').replace('ГБДОУ ', '')}
                            </td>
                            <td className={`datetime-cell ${isOldData ? 'old-datetime' : ''}`} title={formatDateTime(institution.date_modify)}>
                              {formatDateTime(institution.date_modify)}
                            </td>
                            <td>{institution.n_groups || 0}</td>
                            <td>{institution.n_groups_do || 0}</td>
                            <td>{institution.n_students || 0}</td>
                            <td>{institution.ill_students || 0}</td>
                            <td className={sickPercentage > 25 ? 'high-percentage' : ''}>
                              {sickPercentage.toFixed(1)}%
                            </td>
                            <td>{institution.ill_employees || 0}</td>
                            <td>{institution.n_miss || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="error-toast">
          Ошибка: {error}
          <button onClick={fetchData} className="retry-btn-small">
            ⟳
          </button>
        </div>
      )}
    </div>
  );
}

export default App;